import { firestore } from 'fb';
import { query, collection, where, getDoc, getDocs, orderBy, limit, QueryDocumentSnapshot, Query, updateDoc, increment, doc } from 'firebase/firestore';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { OpinionModel } from '../Opinion.hooks';
import { OpinionFilterVariables } from '../OpinionFilter';
import { OpinionColProps } from './OpinionCol';

const getDocsWithId = async <T,>(query: Query) => {
  const snapshot = await getDocs(query);
  return snapshot.docs.map((o: QueryDocumentSnapshot) => ({ id: o.id, ...o.data() } as unknown as T));
}

export type OpinionsQueryParams = OpinionFilterVariables & {
  isConfirmed?: boolean;
};

export const useOpinionQuery = ({ id }: { id: string }) =>
  useQuery(
    ['opinions', id],
    async () => {
      const ref = doc(firestore, 'opinions', id);
      const snapshot = await getDoc(ref);
      if (!snapshot.exists()) {
        return null;
      }

      if (!snapshot.data().isConfirmed) {
        return null;
      }

      return {
        id: snapshot.id,
        ...snapshot.data(),
      } as OpinionModel;
    });

export const useOpinionsQuery = ({ stock, direction }: OpinionColProps) =>
  useQuery(
    ['opinions', stock.symbol, direction],
    () => {
      const { symbol } = stock;
      const ref = query(
        collection(firestore, 'opinions'),
        where('symbol', '==', symbol),
        where('direction', '==', direction),
        where('isConfirmed', '==', true),
        orderBy('createdAt', 'desc'),
        limit(25), // TODO https://firebase.google.com/docs/firestore/query-data/query-cursors#paginate_a_query
      );
      return getDocsWithId<OpinionModel>(ref);
    });

export const useAllOpinionsQuery = (params: OpinionsQueryParams) => {
  const { sort = 'createdAt', isConfirmed = true } = params || {};
  return useQuery(
    ['opinions', sort, isConfirmed],
    async () => {
      const ref = query(
        collection(firestore, 'opinions'),
        where('isConfirmed', '==', isConfirmed),
        orderBy(sort, 'desc'),
        limit(25), // TODO https://firebase.google.com/docs/firestore/query-data/query-cursors#paginate_a_query
      );
      return getDocsWithId<OpinionModel>(ref)
    });
}

export type OpinionLikeMutationVariables = {
  opinionId: string;
  increaseCount: number;
};

export const useOpinionLikeMutation = () => {
  const client = useQueryClient();
  return useMutation(async ({ opinionId, increaseCount }: OpinionLikeMutationVariables) => {
    const ref = doc(firestore, "opinions", opinionId);

    await updateDoc(ref, {
      likesCount: increment(increaseCount)
    });
  }, {
    onSuccess: () => {
      client.invalidateQueries('opinions');
    }
  })
}
